import { looksSecret, redactSecretsInText } from '../services/ai/secretHeuristics';
import { splitUrlAndParams, urlFromParams } from './urlParams';

/**
 * Credential redaction for "Share in Chat" snapshot payloads (26.08.2026).
 *
 * ── Why this is one pass over the FINISHED payload ──────────────────────────
 *
 * The first cut of this redacted three named fields (`auth`, `headers`,
 * `cookies`) inside the per-request serializer. That architecture fails open:
 * anything `prepareRequestBodyForApi` or `stripFolderIdForPayload` emits now or
 * adds later travels in the clear until somebody remembers to patch the
 * redactor. It had already failed open thirteen times when it was measured —
 * including the case that matters most.
 *
 * That case is worth stating plainly, because it is the reason for the reshape:
 * `defaultRequestAuth()` (utils/requestAuth.js) returns `type: 'inherit'`, so in
 * the ordinary setup a request carries an EMPTY auth block and the real bearer
 * token lives in `collection.settings.collection_auth` — or in
 * `settings.folders[].auth`, or in `settings.collection_variables`. A redactor
 * that only looks at `request.auth` removes nothing at all in the common
 * configuration, while telling the user credentials were removed.
 *
 * So: rules are keyed by POSITION IN THE TREE, not by call site. One
 * implementation covers request-level, folder-level and collection-level
 * occurrences of the same shape, at any depth, in every share type.
 *
 * ── Why a value walk and not a structural allowlist ─────────────────────────
 *
 * A snapshot has to stay lossless or the fork breaks — see chatShare.js, "the
 * fork arrives with every {{placeholder}} broken". An allowlist that rebuilt the
 * payload from named-safe fields would silently DROP each new field instead of
 * leaking it: a different failure, equally real. The walk keeps the shape and
 * changes only values.
 *
 * ── Why the payload deserves this much care ─────────────────────────────────
 *
 * `share_links.payload` is stored server-side as plain jsonb — it is the one
 * place in the product where request credentials rest UNencrypted (the same data
 * stored the normal way goes into Cloak-encrypted `api_requests` columns). A chat
 * share has no expiry and no use cap, no job ever sweeps it, revoking only flips
 * `is_active`, and channel membership is re-checked live — so anyone added to the
 * conversation tomorrow can open a share sent yesterday. A credential that goes
 * in there is a permanent cleartext copy.
 */

// ── R2: header names whose VALUE is a credential ────────────────────────────
//
// Matched on the NAME because the value is opaque: a bearer token and a
// correlation id look alike. Anything not matched travels — a share that
// silently dropped half the headers would break the request it exists to
// reproduce.
//
// This list is a FLOOR, never a whole rule. No name list is ever complete
// (`x-amz-security-token`, `x-goog-api-key`, `private-token` and
// `authentication` all sailed through the first version), which is why every
// row is also put past `looksSecret` below.
const CREDENTIAL_HEADERS =
  /^(authorization|authentication|proxy-authorization|cookie|set-cookie|x-api-key|api-key|apikey|apikey-v2|x-auth-token|x-access-token|x-csrf-token|x-xsrf-token|x-session-token|x-refresh-token|x-auth|x-token|x-secret|private-token|x-amz-security-token|x-goog-api-key|x-goog-iam-authorization-token|x-hub-signature|x-hub-signature-256|x-signature|x-shopify-access-token|x-vault-token|dd-api-key)$/i;

// ── R3/R6: query-param and form-field names whose VALUE is a credential ─────
const CREDENTIAL_PARAMS =
  /^(api[_-]?key|apikey|access[_-]?token|refresh[_-]?token|id[_-]?token|token|auth|key|secret|client[_-]?secret|password|passwd|sig|signature|code|session|sessionid|sid)$/i;

/**
 * `SECRET_VALUE_PATTERNS` in secretHeuristics.js are anchored `^…$`, so
 * `Bearer sk-live-…` does NOT match `^sk-…$` — the heuristic net would have a
 * hole exactly where credentials are commonest. Strip the scheme here, at the
 * call site, rather than loosening the shared heuristic (it is tuned for whole
 * variable values and other callers depend on that).
 */
const AUTH_SCHEME = /^(Bearer|Basic|Token|Digest)\s+/i;

function heuristicallySecret(key, value) {
  if (typeof value !== 'string') return false;
  const bare = value.replace(AUTH_SCHEME, '');
  return looksSecret(key, value) || looksSecret(key, bare);
}

/**
 * A kv row list as the payload carries it: `[{ key, value, enabled }]`.
 *
 * `some`, not `every` (review N7): requiring every element to be an object made
 * one stray primitive disable kv detection for the WHOLE list, so
 * `{ headers: ['x', { key: 'Authorization', value: '…' }] }` sailed through.
 * `redactKvRows` already passes non-objects along untouched, so the permissive
 * direction is the safe one here.
 */
function isKvRows(node) {
  return Array.isArray(node) && node.some((r) => r !== null && typeof r === 'object');
}

/**
 * R2/R3/R6 — the key is kept, the value is not: the recipient still sees WHAT
 * is required and where it goes.
 */
function redactKvRows(rows, namePattern) {
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const key = String(row.key ?? '').trim();
    if (namePattern.test(key) || heuristicallySecret(key, row.value)) {
      return { ...row, value: '' };
    }
    return row;
  });
}

/**
 * R5 — every cookie value goes, regardless of name.
 *
 * Cookie rows are keyed by the COOKIE's name (`session`, `sid`, `__Host-x`), so
 * matching them against the credential-header list finds nothing — exactly the
 * hole the first version of this shipped with. A cookie IS session state; there
 * is no benign cookie value to preserve.
 */
function redactCookieRows(rows) {
  return rows.map((row) =>
    row && typeof row === 'object' ? { ...row, value: '' } : row
  );
}

/**
 * R7 — variables lose the `value` KEY, they are not emptied to `''`.
 *
 * This matches the repo's existing answer to "a secret variable is leaving the
 * device": `normalizeVariable` in utils/yamlWorkspace/serialize.js returns
 * `{ key, enabled, secret }` with no `value` at all. The difference is real on
 * the receiving side — an empty string is a value and substitutes as empty,
 * while an absent value means "you must supply this".
 */
function redactVariableRows(vars) {
  return vars.map((v) => {
    if (!v || typeof v !== 'object') return v;
    if (v.secret) {
      const { value, ...rest } = v;
      return rest;
    }
    if (heuristicallySecret(v.key, v.value)) {
      const { value, ...rest } = v;
      return { ...rest, auto_redacted: true };
    }
    return v;
  });
}

/**
 * R1 — an auth block collapses to its `type`, it is not redacted field by field.
 *
 * `defaultRequestAuth()` populates EVERY variant at once — `bearer`, `apikey`,
 * `basic`, `awssigv4`, `oauth2`. A request whose active type is `bearer` still
 * carries `oauth2.clientSecret` and `basic.password` from a configuration the
 * user abandoned, so a redactor keyed on the ACTIVE type would ship the stale
 * credentials of every other one. It would also leak the next variant somebody
 * adds.
 *
 * The type survives on purpose: "this endpoint needs a bearer token" is a fact
 * about the API, not about the user, and withholding it makes the share
 * unusable while buying nothing. Accepted cost, named so nobody rediscovers it:
 * for `type: 'apikey'` the header/param NAME lives in `auth.apikey.key`, and
 * collapsing loses it — the recipient learns "needs an API key" but not where it
 * goes. Do not add a per-type non-secret allowlist to recover that; it is the
 * "next variant" hole again. R2 keeps header names instead.
 */
function redactAuth(auth) {
  // Positional keying means the VALUE at `auth` is a credential whatever shape
  // it arrives in. `normalizeAuth` guarantees a plain object today, so the first
  // two branches are unreachable — they exist because "unreachable today" is how
  // the last version of this failed open (review N6): both `auth: 'bearer …'`
  // and `auth: [{ key, value }]` were returned verbatim AND never walked.
  if (typeof auth === 'string') return '';
  if (Array.isArray(auth)) return redactKvRows(auth, /.*/);
  if (!auth || typeof auth !== 'object') return auth;
  return auth.type ? { type: auth.type } : {};
}

/**
 * R4a — `scheme://user:password@host` (26.08.2026, review B1).
 *
 * The first cut of R4 inspected query params only, so a URL carrying its
 * credentials in the userinfo component travelled verbatim. The app already
 * knew better: `SECRET_VALUE_PATTERNS[8]` in secretHeuristics.js lists this
 * exact shape as one of the nine things it calls a credential. The redactor
 * simply never asked.
 *
 * It hid well, which is worth recording: whenever the URL ALSO carried a
 * credential-named query param, R4b recomposed through `splitUrlAndParams`,
 * whose `u.origin` drops userinfo as a side effect — so the leak was invisible
 * to any test whose fixture had a redactable param. Only a URL with no params,
 * or with benign ones, showed it.
 *
 * The password runs to the LAST `@` before the host, not the first — the same
 * lookahead `INLINE_SECRET_PATTERNS[1]` uses, and for the same documented
 * reason: `@` is legal in a password, and a class that stops at the first one
 * leaves the tail of the credential visible while still looking redacted, which
 * is worse than not matching at all.
 *
 * The username is KEPT. It is a name, not a value — the same trade R2 makes for
 * header names, so the recipient can see that a username is required.
 */
const URL_USERINFO = /^([a-z][a-z0-9+.-]*:\/\/)([^/\s:@]+)(:[^/\s]+)?@(?=[^/\s@]*(?:[/?#]|$))/i;

function redactUrlUserinfo(url) {
  return url.replace(URL_USERINFO, (match, scheme, user, password) =>
    password ? `${scheme}${user}:@` : match
  );
}

/**
 * R4b — a credential in the query string exists in TWO fields.
 *
 * urlParams.js keeps "params table ↔ URL bar in sync", so redacting the `params`
 * rows without the `url` string leaves the token in plain sight one field over.
 *
 * The URL is only recomposed when a value actually changed: `splitUrlAndParams`
 * round-trips through `new URL()`, which normalises, and a share should not
 * rewrite URLs it had no reason to touch. (Known, harmless asymmetry: when a
 * param DOES force recomposition, `u.origin` drops the kept username too.)
 */
function redactUrl(url) {
  if (typeof url !== 'string' || !url) return url;
  const stripped = redactUrlUserinfo(url);
  const { params } = splitUrlAndParams(stripped);
  if (params.length === 0) return stripped;
  const redacted = redactKvRows(params, CREDENTIAL_PARAMS);
  if (redacted.every((p, i) => p.value === params[i].value)) return stripped;
  return urlFromParams(stripped, redacted);
}

/** Variable lists, wherever the payload puts them. */
const VARIABLE_KEYS = new Set([
  'globals',
  'workspace_globals',
  'variables',
  'collection_variables',
]);

/**
 * R8 — `body`, `pre_request_script` and `test_script` are NOT transformed.
 *
 * A best-effort rewrite of a script produces a script that runs and does the
 * wrong thing, silently, on the recipient's machine; silent corruption is worse
 * than the disclosure it prevents. `40-conventions.md` already settles the body
 * half ("raw/unparseable request bodies are not redacted"). They are detected
 * and disclosed instead — see `findUnredactableRisks`.
 */
const UNREDACTABLE_FIELDS = [
  ['body', 'body'],
  ['pre_request_script', 'pre_request_script'],
  ['test_script', 'test_script'],
  // Collection documentation is free text that travels verbatim inside
  // `settings` and was covered by no rule and named by nothing (review N2).
  // `description` is the same class and is included for the same reason.
  ['documentation', 'documentation'],
  ['description', 'description'],
  // The pre-serialization spellings, so risks can be read off a raw sidebar
  // item without building a payload first.
  ['preRequestScript', 'pre_request_script'],
  ['testScript', 'test_script'],
];

/**
 * Credential-shaped `key: value` inside free text (26.08.2026, review N1).
 *
 * `redactSecretsInText` alone was not enough as a detector, and the way it
 * failed is the same way the draft failed: its R8 fixtures were drawn from the
 * shapes it already matched, so it passed its own tests while missing the two
 * commonest ones. `INLINE_SECRET_PATTERNS[3]` anchors the key on
 * start-of-string/whitespace/`;,&(`, so a JSON key in quotes never anchors:
 *
 *   {"password":"hunter2-real-secret"}          -> missed
 *   pm.environment.set('token', 'aaaabbbb…')    -> missed
 *   client_secret=abc&grant_type=x              -> caught (a fixture shape)
 *
 * So this is enumerated from what a real request body and a real pre-request
 * script LOOK LIKE — JSON, XML, urlencoded, and the `pm.*.set(name, value)`
 * call — rather than from the matcher. It lives here, as this module's own
 * predicate, rather than loosening the shared heuristic that other callers
 * depend on.
 *
 * A `{{placeholder}}` is a pointer, not a secret: excluded, matching
 * `looksSecret`'s own carve-out.
 */
const CREDENTIAL_KV_IN_TEXT =
  /\b(password|passwd|pwd|secret|token|api[_-]?key|apikey|access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|auth[_-]?token|private[_-]?key|credential|authorization)\b["'\s\]]*[:=,>]\s*["'`]?(?!\{\{)[^\s"'`,;)}\]<]{6,}/i;

/** Used as a PREDICATE only — never as a rewriter. See the R8 note above. */
function textLooksLikeItCarriesASecret(text) {
  if (typeof text !== 'string' || !text) return false;
  return redactSecretsInText(text) !== text || CREDENTIAL_KV_IN_TEXT.test(text);
}

function walk(node, key) {
  if (Array.isArray(node)) {
    if (isKvRows(node)) {
      if (key === 'headers') return redactKvRows(node, CREDENTIAL_HEADERS);
      if (key === 'params' || key === 'form_data_fields') {
        return redactKvRows(node, CREDENTIAL_PARAMS);
      }
      if (key === 'cookies') return redactCookieRows(node);
      if (VARIABLE_KEYS.has(key)) return redactVariableRows(node);
    }
    return node.map((child) => walk(child, key));
  }

  if (node && typeof node === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      let value;
      if (k === 'auth' || k === 'collection_auth') value = redactAuth(v);
      else if (k === 'url') value = redactUrl(v);
      else value = walk(v, k);
      // `out[k] = …` on an object literal SETS THE PROTOTYPE when k is
      // `__proto__`, silently dropping the subtree (review N10). Data loss, not
      // a leak, and only reachable from re-imported JSON — but a redactor that
      // deletes data it did not understand is not what anyone wants either.
      Object.defineProperty(out, k, {
        value,
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return out;
  }

  return node;
}

/**
 * Redact credentials from a finished snapshot payload.
 *
 * Pure — returns a new tree, the input is untouched. Applied once, at the
 * `buildSnapshotPayload` return boundary, so every share type and every future
 * field is covered by the same call.
 */
export function redactSnapshotPayload(payload) {
  return walk(payload, null);
}

/**
 * Fields redaction deliberately cannot cover (R8): a credential typed straight
 * into a request body or a script.
 *
 * The precedent is `findExposedSecretKeys` (utils/yamlWorkspace/serialize.js),
 * which exists solely to NAME what is exposed and leave the decision to the
 * user. `redactSecretsInText` is used here as a PREDICATE only, never as a
 * rewriter — see the R8 note above for why.
 *
 * Accepts either a built payload or a raw sidebar item (both key spellings).
 *
 * @returns {{ requestName: string, fields: string[] }[]}
 */
export function findUnredactableRisks(root) {
  const hits = [];
  const seen = new Set();

  /**
   * `contextName` is the nearest enclosing named node. Without it,
   * `collection.settings.documentation` — which has no `name` of its own —
   * would be reported as "Untitled" rather than as the collection it belongs to.
   */
  const visit = (node, contextName) => {
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, contextName));
      return;
    }
    if (!node || typeof node !== 'object') return;

    const name =
      typeof node.name === 'string' && node.name.trim() ? node.name.trim() : contextName;

    const fields = [];
    for (const [prop, label] of UNREDACTABLE_FIELDS) {
      if (textLooksLikeItCarriesASecret(node[prop]) && !fields.includes(label)) {
        fields.push(label);
      }
    }
    if (fields.length > 0) {
      const requestName = name || 'Untitled';
      const dedupe = `${requestName}::${fields.join(',')}`;
      if (!seen.has(dedupe)) {
        seen.add(dedupe);
        hits.push({ requestName, fields });
      }
    }

    Object.values(node).forEach((child) => visit(child, name));
  };

  visit(root, null);
  return hits;
}
